import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { api } from '@/api';
import { toast } from 'sonner';
import { Copy, Mail, CheckCircle, Info, AlertTriangle, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EmailToEventPanel({ eventId }) {
  const [emailAddress, setEmailAddress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [mailgunStatus, setMailgunStatus] = useState(null);

  useEffect(() => {
    loadData();
  }, [eventId]);

  const loadData = async () => {
    try {
      // Load mailgun status first using direct axios call
      const statusRes = await axios.get(`${API_URL}/api/webhook/mailgun/status`).catch(() => ({ data: { configured: false } }));
      setMailgunStatus(statusRes.data);
      
      // Then try to load event email address using direct axios call with token
      const token = localStorage.getItem('token');
      const emailRes = await axios.get(`${API_URL}/api/events/${eventId}/email-address`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailAddress(emailRes.data);
    } catch (error) {
      console.error('Failed to load event email address:', error?.response?.status, error?.message);
      // Email address might not exist yet for this event - that's okay
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!emailAddress?.email_address) return;
    
    const textToCopy = emailAddress.email_address;
    
    // Try modern clipboard API first
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(textToCopy);
        setCopied(true);
        toast.success('Email address copied to clipboard');
        setTimeout(() => setCopied(false), 2000);
        return;
      } catch (err) {
        console.warn('Clipboard API failed, trying fallback');
      }
    }
    
    // Fallback for older browsers or non-secure context
    try {
      const textArea = document.createElement('textarea');
      textArea.value = textToCopy;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        setCopied(true);
        toast.success('Email address copied to clipboard');
        setTimeout(() => setCopied(false), 2000);
      } else {
        throw new Error('execCommand failed');
      }
    } catch (error) {
      // Final fallback - prompt user to copy manually
      toast.error('Could not copy automatically. Please select and copy: ' + textToCopy);
    }
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-sm p-4">
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
          <p className="text-sm text-blue-600">Loading email address...</p>
        </div>
      </div>
    );
  }

  const isActive = mailgunStatus?.configured && emailAddress;

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-sm p-6">
      <div className="flex items-center gap-2 mb-4">
        <Mail className="h-5 w-5 text-blue-600" />
        <h3 className="text-lg font-barlow font-bold uppercase text-blue-900">
          Email-to-Event (CC/BCC)
        </h3>
        <Badge className={`text-xs ${isActive ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-amber-100 text-amber-700 border-amber-200'}`}>
          {isActive ? 'Active' : emailAddress ? 'Pending Mailgun Setup' : 'Generate Address'}
        </Badge>
      </div>

      <div className="space-y-4">
        <div className="flex gap-2">
          <Input
            value={emailAddress?.email_address || ''}
            readOnly
            onClick={(e) => e.target.select()}
            className="font-mono text-sm bg-white border-blue-200 text-blue-800 cursor-text"
            data-testid="event-email-address"
          />
          <Button
            onClick={handleCopy}
            variant="outline"
            className={`border-blue-300 hover:bg-blue-100 transition-colors ${
              copied ? 'bg-green-50 border-green-300 text-green-700' : 'text-blue-700'
            }`}
            data-testid="copy-email-button"
          >
            {copied ? (
              <>
                <CheckCircle className="h-4 w-4 mr-1" />
                Copied
              </>
            ) : (
              <>
                <Copy className="h-4 w-4 mr-1" />
                Copy
              </>
            )}
          </Button>
        </div>

        {/* Status Notice */}
        {isActive ? (
          <div className="flex items-start gap-2 p-3 bg-emerald-50 rounded-sm border border-emerald-200">
            <CheckCircle className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-emerald-800">
              <p className="font-medium mb-1">Inbound Email Active</p>
              <p className="text-emerald-700 text-xs">
                Emails sent to this address will be automatically captured in this event's correspondence.
                Domain: <span className="font-mono">{mailgunStatus?.domain}</span>
              </p>
            </div>
          </div>
        ) : emailAddress ? (
          <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-sm border border-amber-200">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-800">
              <p className="font-medium mb-1">Mailgun Route Required</p>
              <p className="text-amber-700 text-xs">
                Email address is ready. Set up a Mailgun route to auto-capture inbound emails.
              </p>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-sm border border-blue-200">
            <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Generate Email Address</p>
              <p className="text-blue-700 text-xs">
                Click the button below to generate a unique email address for this event.
              </p>
            </div>
          </div>
        )}

        <div className="flex items-start gap-2 p-3 bg-white/60 rounded-sm border border-blue-100">
          <Info className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-700">
            <p className="font-medium mb-1">How to use:</p>
            <ol className="list-decimal list-inside space-y-1 text-blue-600">
              <li>Copy this email address</li>
              <li>Add it to <strong>CC</strong> or <strong>BCC</strong> when sending emails in Outlook or Gmail</li>
              <li>Emails will be automatically logged to this event's correspondence</li>
              <li>Build a complete audit trail for your delay claim</li>
            </ol>
          </div>
        </div>

        <div className="flex items-center gap-4 text-xs text-blue-500">
          <span className="flex items-center gap-1">
            <span className={`w-2 h-2 rounded-full ${isActive ? 'bg-green-400' : 'bg-amber-400'}`}></span>
            {isActive ? 'Active' : 'Pending Setup'}
          </span>
          <span>Code: {emailAddress?.email_code}</span>
          <span>Created: {new Date(emailAddress?.created_at).toLocaleDateString()}</span>
        </div>
      </div>
    </div>
  );
}
